import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SportsSoccer as SportsSoccerIcon } from "@mui/icons-material";
import { Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRetrievePlayersByTeamIds } from "../ApiProvider";
import { useBulkSaveGameweekResults, useRetrieveSeasonMatches, } from "../api/SeasonApiProvider";
import { EmptyState } from "../common/EmptyState";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { LeagueSeasonGuardFallback, useLeagueSeasonGuard, } from "../seasons/useLeagueSeasonGuard";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { GameweekResultsForm, } from "./GameweekResultsForm";
export const EnterGameweekResults = () => {
    const { leagueId, seasonId, round } = useNumericParams();
    const navigate = useNavigate();
    // The guard resolves permissions → admin (canModify) → season, so a non-admin
    // gets the forbidden fallback and never reaches the form (LS-514: admin-only).
    const guard = useLeagueSeasonGuard({ leagueId, seasonId });
    const { data: matches, isLoading: matchesLoading, isError: matchesError, error, } = useRetrieveSeasonMatches({ seasonId });
    const roundMatches = useMemo(() => (matches ?? []).filter((m) => m.round === round), [matches, round]);
    // The LeagueMatch carries only player IDs, so fetch each team's roster to
    // resolve names for the expandable stat rows.
    const teamIds = useMemo(() => {
        const ids = new Set();
        roundMatches.forEach((m) => {
            ids.add(m.homeTeam.id);
            ids.add(m.awayTeam.id);
        });
        return [...ids];
    }, [roundMatches]);
    const { rostersByTeamId, isLoading: rostersLoading, isError: rostersError, } = useRetrievePlayersByTeamIds({
        teamIds,
        enabled: guard.status === "ready",
    });
    const back = () => navigate(`/leagues/${leagueId}/seasons/${seasonId}?tab=fixtures`);
    const mutation = useBulkSaveGameweekResults({ onSuccess: back });
    if (guard.status !== "ready") {
        return _jsx(LeagueSeasonGuardFallback, { guard: guard });
    }
    if (matchesLoading || rostersLoading) {
        return _jsx(Loading, {});
    }
    if (matchesError || rostersError) {
        return _jsx(ErrorView, { error: error });
    }
    // Group the gameweek by division, highest-ranked (lowest tier number) first,
    // then kick-off. The form renders a section header per division.
    const divisions = guard.season.divisions ?? [];
    const divisionsById = {};
    divisions.forEach((d) => {
        divisionsById[d.id] = { name: d.name };
    });
    const tierOf = (divisionId) => divisions.find((d) => d.id === divisionId)?.tier ?? 999;
    const orderedMatches = [...roundMatches].sort((a, b) => {
        const tierDiff = tierOf(a.division) - tierOf(b.division);
        if (tierDiff !== 0)
            return tierDiff;
        return new Date(a.kickOff).getTime() - new Date(b.kickOff).getTime();
    });
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: mutation.isError, message: "Failed to save results. Please check each row and try again." }), _jsx(Typography, { variant: "h6", gutterBottom: true, children: _jsxs("strong", { children: ["Enter Results \u2014 Gameweek ", round] }) }), roundMatches.length === 0 ? (_jsx(EmptyState, { icon: _jsx(SportsSoccerIcon, {}), heading: "No fixtures this gameweek", description: "Add fixtures for this gameweek before entering results." })) : (_jsx(GameweekResultsForm, { matches: orderedMatches, rostersByTeamId: rostersByTeamId, divisionsById: divisionsById, onSubmit: (results) => mutation.mutate(results), isSubmitting: mutation.isLoading, onCancel: back }))] }));
};
